import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { bare, setCsrf } from "@/lib/api";

const AuthCtx = createContext(null);

/**
 * Cookie-based sessions.
 *
 *  - Login POST /auth/login (remember=true|false) sets httpOnly session cookies.
 *    The response body returns a csrf_token which we keep in memory for the lifetime
 *    of this tab; the backend ALSO sets a readable `lwco_csrf` cookie so the SPA can
 *    pick it up after a refresh.
 *  - Logout POST /auth/logout revokes the session server-side AND clears the cookies.
 *  - No bearer token is ever stored in localStorage/sessionStorage.
 *  - Blank login form, no auto-fill. If GET /auth/me 401s AND the one-shot refresh
 *    also fails, the browser lands on /login.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Hard safety: if the server never responds (DNS, offline, hung refresh),
    // the SPA must still exit "Loading…" and show the login screen within 10 s.
    let timed = false;
    const timer = setTimeout(() => { timed = true; setUser(null); setLoading(false); }, 10000);
    try {
      // 1) Try /auth/me on the current session.
      let data;
      try {
        ({ data } = await api.get("/auth/me"));
      } catch (e) {
        if (e?.response?.status !== 401) throw e;
        // 2) Access session may have expired but the refresh cookie may still be
        //    valid (classic 'Keep me signed in' case). One-shot refresh on the
        //    bare axios (no interceptor → no recursion), then one retry of /auth/me.
        const r = await bare.post("/auth/refresh");
        if (r?.data?.csrf_token) setCsrf(r.data.csrf_token);
        ({ data } = await api.get("/auth/me"));
      }
      if (!timed) setUser(data);
    } catch {
      if (!timed) setUser(null);
    } finally {
      clearTimeout(timer);
      if (!timed) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password, remember = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember: !!remember });
    if (data.csrf_token) setCsrf(data.csrf_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("hub_worker_job");
    setCsrf("");
    setUser(null);
    window.location.href = "/login";
  }, []);

  const logoutAllDevices = useCallback(async () => {
    try { await api.post("/auth/logout-all"); } catch {}
    localStorage.removeItem("hub_worker_job");
    setCsrf("");
    setUser(null);
    window.location.href = "/login";
  }, []);

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, logout, logoutAllDevices, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() { return useContext(AuthCtx); }
